import { PublicationFieldDiffType } from '@/enums';
import { levenshtein } from 'wuzzy';

/**
 * Compares two publications and returns a list of differences.
 *
 * @param {Publication} mainCopy The main copy (most complete version) of the publication
 * @param {Publication} otherCopy A potential copy of the publication
 * @returns {Array} An array of `Object` with the following properties:
 *  - `field`: the field in question
 *  - `diffType`: the type of difference (see `PublicationFieldDiffType`)
 *  - `score`: the similarity score if applicable (0-1)
 */
export function comparePublications(mainCopy, otherCopy) {
  const ret = [];

  ret.push({
    field: 'doi',
    diffType: computeDoiDiff(mainCopy.doi, otherCopy.doi),
  });

  ret.push({
    field: 'title',
    diffType: computeDiffViaNormalisation(
      mainCopy.title,
      otherCopy.title
    ),
  });

  ret.push({
    field: 'year',
    diffType: computeYearDiff(mainCopy.year, otherCopy.year),
  });

  ret.push({
    field: 'authors',
    diffType: computeAuthorsDiffViaNormalisation(
      mainCopy.authors,
      otherCopy.authors
    ),
  });

  ret.push({
    field: 'placePublished',
    diffType: computeDiffViaNormalisation(
      mainCopy.placePublished,
      otherCopy.placePublished
    ),
  });

  ret.push({
    field: 'abstract',
    ...computeAbstractDiffAndScore(
      mainCopy.abstract,
      otherCopy.abstract
    ),
  });

  return ret.map((diff) => ({
    ...diff,
    left: mainCopy[diff.field],
    right: otherCopy[diff.field],
  }));
}

/**
 * Normalises both DOIs and compares them.
 *
 * @param {string} mainCopyDoi The DOI of the main copy
 * @param {*} otherCopyDoi The DOI of the other copy
 * @returns {PublicationFieldDiffType} The type of difference
 */
export function computeDoiDiff(mainCopyDoi, otherCopyDoi) {
  const mainCopyHasDoi = mainCopyDoi && mainCopyDoi.trim().length > 0;
  const otherCopyHasDoi =
    otherCopyDoi && otherCopyDoi.trim().length > 0;

  if (!mainCopyHasDoi && otherCopyHasDoi) {
    return PublicationFieldDiffType.ADD;
  } else if (mainCopyHasDoi && !otherCopyHasDoi) {
    return PublicationFieldDiffType.MISSING;
  }

  const mainCopyDoiNormalised = normaliseDoi(mainCopyDoi);
  const otherCopyDoiNormalised = normaliseDoi(otherCopyDoi);

  if (mainCopyDoiNormalised === otherCopyDoiNormalised) {
    return PublicationFieldDiffType.MATCH;
  }

  return PublicationFieldDiffType.MISMATCH;
}

/**
 * Normalises the given DOI by removing any known link prefix
 *
 * @param {string} doi The DOI to normalise
 * @returns {string} The normalised DOI
 */
export function normaliseDoi(doi) {
  const doiLinkPrefixes = ['https://doi.org/', 'https://dx.doi.org/'];
  return doiLinkPrefixes.reduce(
    (acc, prefix) => acc?.replace(prefix, ''),
    doi?.trim()?.toLowerCase()
  );
}

/**
 * Compares two years and returns a difference type.
 *
 * @param {int} mainCopyYear The main copy year
 * @param {int} otherCopyYear The other copy year
 * @returns {PublicationFieldDiffType} The difference type
 */
export function computeYearDiff(mainCopyYear, otherCopyYear) {
  if (!mainCopyYear && otherCopyYear) {
    return PublicationFieldDiffType.ADD;
  } else if (mainCopyYear && !otherCopyYear) {
    return PublicationFieldDiffType.MISSING;
  } else if (mainCopyYear !== otherCopyYear) {
    return PublicationFieldDiffType.MISMATCH;
  } else {
    return PublicationFieldDiffType.MATCH;
  }
}

/**
 * Normalises each author in the lists and compares them.
 *
 * @param {*} mainCopyAuthors The main copy authors
 * @param {*} otherCopyAuthors The other copy authors
 * @returns {PublicationFieldDiffType} The difference type
 */
export function computeAuthorsDiffViaNormalisation(
  mainCopyAuthors,
  otherCopyAuthors
) {
  const normalisedAuthorsMainCopy = (mainCopyAuthors ?? []).map(
    normalise
  );
  const normalisedAuthorsOtherCopy = (otherCopyAuthors ?? []).map(
    normalise
  );

  const mainCopyAuthorsSet = new Set(normalisedAuthorsMainCopy);
  const otherCopyAuthorsSet = new Set(normalisedAuthorsOtherCopy);

  const mainCopyAuthorsOnly = [...mainCopyAuthorsSet].filter(
    (author) => !otherCopyAuthorsSet.has(author)
  );
  const otherCopyAuthorsOnly = [...otherCopyAuthorsSet].filter(
    (author) => !mainCopyAuthorsSet.has(author)
  );

  if (
    mainCopyAuthorsOnly.length > 0 &&
    otherCopyAuthorsOnly.length > 0
  ) {
    return PublicationFieldDiffType.MISMATCH;
  } else if (mainCopyAuthorsOnly.length > 0) {
    return PublicationFieldDiffType.MISSING;
  } else if (otherCopyAuthorsOnly.length > 0) {
    return PublicationFieldDiffType.ADD;
  } else {
    return PublicationFieldDiffType.MATCH;
  }
}

/**
 * Normalises both string fields and compares them.
 *
 * @param {string} a The main copy field value
 * @param {string} b The other copy field value
 * @returns {PublicationFieldDiffType} The type of difference
 */
export function computeDiffViaNormalisation(a, b) {
  const normalisedA = normalise(a);
  const normalisedB = normalise(b);

  if (normalisedA.length === 0 && normalisedB.length > 0) {
    return PublicationFieldDiffType.ADD;
  } else if (normalisedA.length > 0 && normalisedB.length === 0) {
    return PublicationFieldDiffType.MISSING;
  } else if (normalisedA !== normalisedB) {
    return PublicationFieldDiffType.MISMATCH;
  } else {
    return PublicationFieldDiffType.MATCH;
  }
}

/**
 * Normalises both abstracts then compares them using the Levenshtein distance.
 *
 * @param {string} mainCopyAbstract The main copy's abstract
 * @param {string} otherCopyAbstract The other copy's abstract
 * @returns {Object} An object with the following properties:
 * - `diffType`: the type of difference (see `PublicationFieldDiffType`)
 * - `score`: the Levenshtein distance between the two abstracts
 */
export function computeAbstractDiffAndScore(
  mainCopyAbstract,
  otherCopyAbstract
) {
  const normalisedA = normalise(mainCopyAbstract ?? '');
  const normalisedB = normalise(otherCopyAbstract ?? '');

  if (normalisedA.length === 0 && normalisedB.length > 0) {
    return { diffType: PublicationFieldDiffType.ADD, score: 0 };
  } else if (normalisedA.length > 0 && normalisedB.length === 0) {
    return { diffType: PublicationFieldDiffType.MISSING, score: 0 };
  } else if (normalisedA !== normalisedB) {
    const score = levenshtein(normalisedA, normalisedB);
    return { diffType: PublicationFieldDiffType.MISMATCH, score };
  } else {
    return { diffType: PublicationFieldDiffType.MATCH, score: 1 };
  }
}

/**
 * Normalises a string for comparison.
 * Follows the same procedure as the `StringUtils.normalise` method in the Java codebase.
 *
 * @param {string} value The value to be normalised
 * @returns {string} Normalised value
 */
export function normalise(value) {
  // Convert to lower case.
  let ret = value.toLowerCase();

  // Remove all occurrences whitespace, new lines, and carriage returns.
  ret = ret.replace(/[\s\n\r]+/g, '');

  // Remove all punctuation.
  ret = ret.replace(/[.,/#!$%^&*;:{}=\-_`~()?<>'"@+|]/g, '');

  return ret;
}
